import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  AssociateSoftwareTokenCommand,
  AuthFlowType,
  ChallengeNameType,
  ConfirmForgotPasswordCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  InitiateAuthCommand,
  ResendConfirmationCodeCommand,
  RespondToAuthChallengeCommand,
  SetUserMFAPreferenceCommand,
  SignUpCommand,
  VerifySoftwareTokenCommand,
  VerifySoftwareTokenResponseType,
  type AuthenticationResultType,
} from '@aws-sdk/client-cognito-identity-provider';
import { err, ok, type Result } from 'neverthrow';

import { queryClient } from '@/lib/query/client';

import { authConfig } from './config';
import { cognitoClient } from './cognito';
import {
  mapConfirmError,
  mapForgotPasswordError,
  mapMfaChallengeError,
  mapMfaSetupError,
  mapResendError,
  mapResetPasswordError,
  mapSignInError,
  mapSignUpError,
  type ConfirmError,
  type ForgotPasswordError,
  type MfaChallengeError,
  type MfaSetupError,
  type ResendError,
  type ResetPasswordError,
  type SignInError,
  type SignUpError,
} from './errors';
import { decodeIdToken } from './jwt';
import { clearTokens, isNearExpiry, loadTokens, saveTokens, type StoredTokens } from './tokens';
import type { AuthState, User } from './types';

export interface SignUpResult {
  /** Cognito's stable user identifier — `sub` in JWT claims. */
  readonly userSub: string;
  /** False when email verification is still pending (always false at MVP). */
  readonly userConfirmed: boolean;
}

/**
 * `signIn` returns one of three shapes. The MFA branch carries the Cognito
 * `Session` token and the username so the UI can pivot to a code-entry
 * step and round-trip them through `respondToMfaChallenge`. Cognito's
 * challenge `Session` lives ~3 minutes — if it expires the user has to
 * re-enter their password.
 */
export type SignInOutcome =
  | { readonly kind: 'authenticated'; readonly user: User }
  | { readonly kind: 'mfa-required'; readonly session: string; readonly username: string };

/** Returned by `setupTotp`. The `otpauthUri` is renderable as a QR code. */
export interface TotpSetup {
  /** Raw base32 secret. Show as a human-readable fallback under the QR code. */
  readonly secretCode: string;
  /** Full otpauth:// URI to encode into the QR code. */
  readonly otpauthUri: string;
}

interface AuthContextValue {
  readonly state: AuthState;
  // Declared as arrow-function types — no `this` binding, so destructuring
  // at call sites (`const { signIn } = useAuth()`) doesn't trip
  // typescript-eslint's unbound-method rule.
  readonly signIn: (email: string, password: string) => Promise<Result<SignInOutcome, SignInError>>;
  readonly respondToMfaChallenge: (
    username: string,
    session: string,
    code: string,
  ) => Promise<Result<User, MfaChallengeError>>;
  readonly signUp: (email: string, password: string) => Promise<Result<SignUpResult, SignUpError>>;
  readonly confirmSignUp: (email: string, code: string) => Promise<Result<void, ConfirmError>>;
  readonly resendConfirmationCode: (email: string) => Promise<Result<void, ResendError>>;
  readonly forgotPassword: (email: string) => Promise<Result<void, ForgotPasswordError>>;
  readonly confirmForgotPassword: (
    email: string,
    code: string,
    newPassword: string,
  ) => Promise<Result<void, ResetPasswordError>>;
  readonly setupTotp: () => Promise<Result<TotpSetup, MfaSetupError>>;
  readonly verifyTotpAndEnable: (
    code: string,
    friendlyName: string,
  ) => Promise<Result<void, MfaSetupError>>;
  readonly signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Cognito returns AccessToken, IdToken, RefreshToken, ExpiresIn (seconds).
 * Pull them into our StoredTokens shape with an absolute expiry timestamp.
 */
function tokensFromAuthResult(result: AuthenticationResultType): StoredTokens | null {
  const { AccessToken, IdToken, RefreshToken, ExpiresIn } = result;
  if (!AccessToken || !IdToken || !RefreshToken || typeof ExpiresIn !== 'number') {
    return null;
  }
  return {
    accessToken: AccessToken,
    idToken: IdToken,
    refreshToken: RefreshToken,
    expiresAt: Date.now() + ExpiresIn * 1000,
  };
}

function userFromIdToken(idToken: string): User {
  const claims = decodeIdToken(idToken);
  return { username: claims.sub, email: claims.email };
}

/**
 * Use the refresh token to get a fresh access + id token. Cognito doesn't
 * rotate refresh tokens, so we keep the existing one.
 */
async function refreshTokens(stored: StoredTokens): Promise<StoredTokens | null> {
  try {
    const response = await cognitoClient.send(
      new InitiateAuthCommand({
        AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
        ClientId: authConfig.userPoolClientId,
        AuthParameters: { REFRESH_TOKEN: stored.refreshToken },
      }),
    );
    const result = response.AuthenticationResult;
    if (!result?.AccessToken || !result.IdToken || typeof result.ExpiresIn !== 'number') {
      return null;
    }
    return {
      accessToken: result.AccessToken,
      idToken: result.IdToken,
      refreshToken: stored.refreshToken,
      expiresAt: Date.now() + result.ExpiresIn * 1000,
    };
  } catch {
    return null;
  }
}

async function restoreSession(): Promise<User | null> {
  const stored = loadTokens();
  if (!stored) return null;

  let tokens = stored;
  if (isNearExpiry(stored)) {
    const refreshed = await refreshTokens(stored);
    if (!refreshed) {
      clearTokens();
      return null;
    }
    saveTokens(refreshed);
    tokens = refreshed;
  }

  try {
    return userFromIdToken(tokens.idToken);
  } catch {
    // Malformed token — clear and start fresh.
    clearTokens();
    return null;
  }
}

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'initializing' });

  useEffect(() => {
    let cancelled = false;
    void restoreSession().then((user) => {
      if (cancelled) return;
      setState(user ? { status: 'authenticated', user } : { status: 'unauthenticated' });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(
    async (email: string, password: string): Promise<Result<SignInOutcome, SignInError>> => {
      try {
        const response = await cognitoClient.send(
          new InitiateAuthCommand({
            AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
            ClientId: authConfig.userPoolClientId,
            AuthParameters: { USERNAME: email, PASSWORD: password },
          }),
        );

        // Happy path: Cognito returned tokens immediately. Store them and
        // flip to authenticated state.
        if (response.AuthenticationResult) {
          const tokens = tokensFromAuthResult(response.AuthenticationResult);
          if (!tokens) {
            return err({
              kind: 'unknown',
              message: 'Cognito returned an incomplete token set.',
            });
          }
          saveTokens(tokens);
          const user = userFromIdToken(tokens.idToken);
          setState({ status: 'authenticated', user });
          return ok({ kind: 'authenticated', user });
        }

        // TOTP MFA challenge — the user has SOFTWARE_TOKEN_MFA enrolled.
        // Return the Session so the UI can collect the 6-digit code and
        // round-trip through respondToMfaChallenge. Cognito's challenge
        // Session expires in ~3 minutes.
        if (response.ChallengeName === ChallengeNameType.SOFTWARE_TOKEN_MFA && response.Session) {
          // ChallengeParameters.USER_ID_FOR_SRP carries the canonical
          // username (Cognito sub) we need to pass back in
          // RespondToAuthChallenge. Fall back to the email if it's
          // missing — shouldn't happen in practice but keeps the call
          // shape valid.
          const username = response.ChallengeParameters?.USER_ID_FOR_SRP ?? email;
          return ok({ kind: 'mfa-required', session: response.Session, username });
        }

        // Cognito returned some other challenge (NEW_PASSWORD_REQUIRED,
        // SMS_MFA, MFA_SETUP, etc.). None are part of our supported
        // flow today — surface as unknown so the UI tells the user
        // rather than silently doing nothing.
        return err({
          kind: 'unknown',
          message: `Sign-in requires an additional step (${response.ChallengeName ?? 'unknown'}) which isn't supported yet.`,
        });
      } catch (caught) {
        return err(mapSignInError(caught, email));
      }
    },
    [],
  );

  const respondToMfaChallenge = useCallback(
    async (
      username: string,
      session: string,
      code: string,
    ): Promise<Result<User, MfaChallengeError>> => {
      try {
        const response = await cognitoClient.send(
          new RespondToAuthChallengeCommand({
            ClientId: authConfig.userPoolClientId,
            ChallengeName: ChallengeNameType.SOFTWARE_TOKEN_MFA,
            Session: session,
            ChallengeResponses: {
              USERNAME: username,
              SOFTWARE_TOKEN_MFA_CODE: code,
            },
          }),
        );

        if (!response.AuthenticationResult) {
          // Cognito returned a follow-up challenge — we don't chain
          // challenges today.
          return err({
            kind: 'unknown',
            message: 'MFA challenge returned another challenge instead of tokens.',
          });
        }

        const tokens = tokensFromAuthResult(response.AuthenticationResult);
        if (!tokens) {
          return err({
            kind: 'unknown',
            message: 'Cognito returned an incomplete token set after MFA.',
          });
        }
        saveTokens(tokens);
        const user = userFromIdToken(tokens.idToken);
        setState({ status: 'authenticated', user });
        return ok(user);
      } catch (caught) {
        return err(mapMfaChallengeError(caught));
      }
    },
    [],
  );

  const signUp = useCallback(
    async (email: string, password: string): Promise<Result<SignUpResult, SignUpError>> => {
      try {
        const response = await cognitoClient.send(
          new SignUpCommand({
            ClientId: authConfig.userPoolClientId,
            Username: email,
            Password: password,
            // Cognito stores only email + password. Name, country,
            // marketing opt-in, and all other profile fields go to
            // DynamoDB once the backend API lands (ADR 0005). Until
            // then, the wizard collects them and discards on completion.
            UserAttributes: [{ Name: 'email', Value: email }],
          }),
        );
        // UserSub is always present on a successful SignUp response.
        if (!response.UserSub) {
          return err({
            kind: 'unknown',
            message: 'Cognito returned a sign-up response without a user identifier.',
          });
        }
        return ok({
          userSub: response.UserSub,
          userConfirmed: response.UserConfirmed === true,
        });
      } catch (caught) {
        return err(mapSignUpError(caught));
      }
    },
    [],
  );

  const confirmSignUp = useCallback(
    async (email: string, code: string): Promise<Result<void, ConfirmError>> => {
      try {
        await cognitoClient.send(
          new ConfirmSignUpCommand({
            ClientId: authConfig.userPoolClientId,
            Username: email,
            ConfirmationCode: code,
          }),
        );
        return ok(undefined);
      } catch (caught) {
        return err(mapConfirmError(caught));
      }
    },
    [],
  );

  const resendConfirmationCode = useCallback(
    async (email: string): Promise<Result<void, ResendError>> => {
      try {
        await cognitoClient.send(
          new ResendConfirmationCodeCommand({
            ClientId: authConfig.userPoolClientId,
            Username: email,
          }),
        );
        return ok(undefined);
      } catch (caught) {
        return err(mapResendError(caught));
      }
    },
    [],
  );

  const forgotPassword = useCallback(
    async (email: string): Promise<Result<void, ForgotPasswordError>> => {
      try {
        await cognitoClient.send(
          new ForgotPasswordCommand({
            ClientId: authConfig.userPoolClientId,
            Username: email,
          }),
        );
        return ok(undefined);
      } catch (caught) {
        return err(mapForgotPasswordError(caught));
      }
    },
    [],
  );

  const confirmForgotPassword = useCallback(
    async (
      email: string,
      code: string,
      newPassword: string,
    ): Promise<Result<void, ResetPasswordError>> => {
      try {
        await cognitoClient.send(
          new ConfirmForgotPasswordCommand({
            ClientId: authConfig.userPoolClientId,
            Username: email,
            ConfirmationCode: code,
            Password: newPassword,
          }),
        );
        return ok(undefined);
      } catch (caught) {
        return err(mapResetPasswordError(caught));
      }
    },
    [],
  );

  const setupTotp = useCallback(async (): Promise<Result<TotpSetup, MfaSetupError>> => {
    try {
      const tokens = loadTokens();
      if (!tokens) {
        return err({ kind: 'session-expired' });
      }
      const response = await cognitoClient.send(
        new AssociateSoftwareTokenCommand({ AccessToken: tokens.accessToken }),
      );
      if (!response.SecretCode) {
        return err({
          kind: 'unknown',
          message: 'Cognito did not return a TOTP secret.',
        });
      }
      const email = userFromIdToken(tokens.idToken).email;
      // otpauth URI is the standard format authenticator apps consume.
      // `issuer` shows in the app's account list; `algorithm`, `digits`,
      // and `period` are Cognito's defaults — including them explicitly
      // matches what Cognito Hosted UI generates and works with every
      // authenticator we've tested (Authy, 1Password, Google Authenticator).
      const issuer = 'Mailchute.ai';
      const otpauthUri =
        `otpauth://totp/${encodeURIComponent(`${issuer}:${email}`)}` +
        `?secret=${response.SecretCode}&issuer=${encodeURIComponent(issuer)}` +
        '&algorithm=SHA1&digits=6&period=30';
      return ok({ secretCode: response.SecretCode, otpauthUri });
    } catch (caught) {
      return err(mapMfaSetupError(caught));
    }
  }, []);

  const verifyTotpAndEnable = useCallback(
    async (code: string, friendlyName: string): Promise<Result<void, MfaSetupError>> => {
      try {
        const tokens = loadTokens();
        if (!tokens) {
          return err({ kind: 'session-expired' });
        }
        const verifyResponse = await cognitoClient.send(
          new VerifySoftwareTokenCommand({
            AccessToken: tokens.accessToken,
            UserCode: code,
            FriendlyDeviceName: friendlyName,
          }),
        );
        if (verifyResponse.Status !== VerifySoftwareTokenResponseType.SUCCESS) {
          return err({ kind: 'code-mismatch' });
        }
        await cognitoClient.send(
          new SetUserMFAPreferenceCommand({
            AccessToken: tokens.accessToken,
            SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true },
          }),
        );
        return ok(undefined);
      } catch (caught) {
        return err(mapMfaSetupError(caught));
      }
    },
    [],
  );

  const signOut = useCallback(() => {
    clearTokens();
    // Drop every per-user query so the next sign-in (potentially as a
    // different user on a shared device) starts from a cold cache.
    queryClient.clear();
    setState({ status: 'unauthenticated' });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      signIn,
      respondToMfaChallenge,
      signUp,
      confirmSignUp,
      resendConfirmationCode,
      forgotPassword,
      confirmForgotPassword,
      setupTotp,
      verifyTotpAndEnable,
      signOut,
    }),
    [
      state,
      signIn,
      respondToMfaChallenge,
      signUp,
      confirmSignUp,
      resendConfirmationCode,
      forgotPassword,
      confirmForgotPassword,
      setupTotp,
      verifyTotpAndEnable,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}
